import { Component, OnInit } from "@angular/core";
import { Router, NavigationEnd } from "@angular/router";
import { Observable } from "rxjs";
import { filter } from "rxjs/operators";

import { SiteConfigService } from "src/app/core/services";
import { IMenu } from "src/app/core/ITypes";

@Component({
  selector: "app-main-navigation",
  templateUrl: "./main-navigation.component.html",
  styleUrls: ["./main-navigation.component.scss"],
})
export class MainNavigationComponent implements OnInit {
  primaryMenuItems = 4;

  // Toggle between menu display modes
  // false: Show first N visible items (original behavior)
  // true: Prioritize specific menu items (HOME, PRODUCT, ORDER, CUSTOMER)
  public usePriorityMenuItems = true;

  // Track active dropdown
  public activeDropdown: string | null = null;

  // Store all menu items
  public allMenuItems: IMenu[] = [];

  // Direct menu items (max : primaryMenuItems)
  public directMenuItems: IMenu[] = [];

  // Dropdown menu items (remaining items)
  public dropdownMenuItems: IMenu[] = [];

  // Current active route
  public currentRoute: string = null;

  // Observable for menu items
  menuItems$: Observable<IMenu[]>;

  // Additional properties
  public currentUrl: string = "";
  public isDropdownOpen: boolean = false;

  constructor(
    private router: Router,
    private siteConfigService: SiteConfigService
  ) {
    // Track current route
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.currentRoute = event.url.split("?")[0];
      });

    // Get menu items from service
    this.menuItems$ = this.siteConfigService.menuItems$;
  }

  ngOnInit(): void {
    // Subscribe to menu items and split into direct and dropdown items
    this.menuItems$.subscribe((menuItems) => {
      if (menuItems && menuItems.length) {
        this.allMenuItems = menuItems;
        this.splitMenuItems(menuItems);
      }
    });

    // Track current route for active highlighting
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.currentUrl = event.url;
      });
  }

  /**
   * Split menu items into direct and dropdown items
   * @param menuItems All menu items from the service
   */
  splitMenuItems(menuItems: IMenu[]): void {
    // Filter visible items
    const visibleItems = menuItems.filter((item) => item.visible !== false);

    if (this.usePriorityMenuItems) {
      // Priority mode: Show specific menu items in primary menu
      // Priority slugs that should be in the primary menu
      const prioritySlugs = ["HOME", "PRODUCT", "ORDER", "CUSTOMER"];

      // Create direct menu items based on priority slugs
      this.directMenuItems = [];

      // First add priority items that are visible
      prioritySlugs.forEach((slug) => {
        const item = visibleItems.find((menuItem) => menuItem.slug === slug);
        if (item) {
          this.directMenuItems.push(item);
        }
      });

      // Limit to primaryMenuItems (4)
      this.directMenuItems = this.directMenuItems.slice(
        0,
        this.primaryMenuItems
      );
    } else {
      // Original mode: Show first N visible items
      // Take first N items for direct display
      this.directMenuItems = visibleItems.slice(0, this.primaryMenuItems);
    }

    // Put all other items in dropdown (items not in directMenuItems)
    this.dropdownMenuItems = visibleItems.filter(
      (item) =>
        !this.directMenuItems.some(
          (directItem) => directItem.slug === item.slug
        )
    );
  }

  /**
   * Toggle dropdown visibility
   * @param dropdownId ID of the dropdown to toggle
   */
  toggleDropdown(dropdownId: string): void {
    this.activeDropdown =
      this.activeDropdown === dropdownId ? null : dropdownId;
  }

  /**
   * Check if dropdown is active
   * @param dropdownId ID of the dropdown to check
   * @returns boolean indicating if dropdown is active
   */
  isDropdownActive(dropdownId: string): boolean {
    return this.activeDropdown === dropdownId;
  }

  /**
   * Check if a route is active
   * @param url Route URL to check
   * @returns boolean indicating if route is active
   */
  isRouteActive(url: string): boolean {
    return this.currentRoute === url;
  }

  /**
   * Check if any dropdown menu item is active
   * @returns boolean indicating if any dropdown menu item is active
   */
  isDropdownMenuActive(): boolean {
    if (!this.currentRoute || !this.dropdownMenuItems.length) {
      return false;
    }

    // Check if any dropdown menu item's URL matches the current route
    return this.dropdownMenuItems.some((item) =>
      this.currentRoute.startsWith(item.url)
    );
  }
}
